import { Box, Typography } from "@mui/material";
import { LinearProgress, useGetList, useGetOne } from "react-admin";
import { autovioColors, cardTitleStyle, overviewCardStyle } from "../../../misc/backofficeTheme";
import { moneyFormatter } from "../../../misc/Money";
import type { BackendUserRecord } from "../../../providers/backendUserProvider";
import {
  getOldestOpenPaymentAgeInDays,
  getOpenPaymentAgeLabel,
  type Invoice,
} from "../../../providers/invoicesProvider";
import type { Student } from "../../../providers/studentsProvider";

export function PaymentsCard(props: { student: Student }) {
  const { student } = props;
  const {
    data: backendUser,
    isLoading: isBackendUserLoading,
    error: backendUserError,
  } = useGetOne<BackendUserRecord>("backendUser", {
    id: student.id,
  });
  const {
    data: invoices,
    isLoading: areInvoicesLoading,
    error: invoicesError,
  } = useGetList<Invoice>("b2cInvoices", {
    filter: { studentId: student.id, excludeType: ["PREPAID_CREDITS"] },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "name", order: "ASC" },
  });
  const accounts =
    typeof backendUser?.payload?.accounts === "object" ? Object.values(backendUser.payload.accounts)[0] : undefined;
  const totalExpenditure =
    (invoices?.filter((invoice) => invoice.status === "PAID").reduce((sum, invoice) => sum + invoice.total, 0) ?? 0) /
    100;
  const balance = parseFloat(accounts?.balance?.balance?.amount ?? "0");
  const oldestOpenPaymentAgeInDays = invoices ? getOldestOpenPaymentAgeInDays(invoices) : undefined;
  const isLoading = isBackendUserLoading || areInvoicesLoading;
  const hasError = !!(backendUserError || invoicesError);

  return (
    <Box sx={{ ...overviewCardStyle, padding: "20px 30px" }}>
      <Typography sx={cardTitleStyle}>Zahlungen</Typography>
      {isLoading ? (
        <LinearProgress timeout={50} sx={{ my: 2 }} />
      ) : hasError ? (
        <Typography sx={{ color: "error.main", fontSize: "14px" }}>
          Zahlungsdaten konnten nicht geladen werden.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <_PaymentAmount label="Gesamtausgaben" amount={totalExpenditure} />
          <Box>
            <_PaymentAmount label="Saldo" amount={balance} redForNegativeValues />
            <_PaymentStatus oldestOpenPaymentAgeInDays={oldestOpenPaymentAgeInDays} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function _PaymentAmount(props: { label: string; amount: number; redForNegativeValues?: boolean }) {
  const { label, amount, redForNegativeValues } = props;
  const color = redForNegativeValues && amount < 0 ? autovioColors.red : undefined;
  const [actualAmount, currency] = moneyFormatter.format(amount).split(/\s+/);
  return (
    <Box>
      <Typography sx={{ color: "text.secondary", fontSize: "12px" }}>{label}</Typography>
      <Typography sx={{ color, fontWeight: 600, fontSize: "15px" }}>
        {actualAmount}
        <Box component="span" sx={{ ml: "4px", fontSize: "12px" }}>
          {currency}
        </Box>
      </Typography>
    </Box>
  );
}

function _PaymentStatus(props: { oldestOpenPaymentAgeInDays?: number }) {
  const { oldestOpenPaymentAgeInDays } = props;

  if (oldestOpenPaymentAgeInDays === undefined || oldestOpenPaymentAgeInDays < 7) {
    return null;
  }

  return (
    <Typography sx={{ color: "text.secondary", fontSize: "13px", lineHeight: 1.3, mt: 0.5 }}>
      Älteste Rechnung: <Box component="span">{getOpenPaymentAgeLabel(oldestOpenPaymentAgeInDays)} offen</Box>
    </Typography>
  );
}
